import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { ActionReducer, Store, provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { IFeatureFlagsAppState, RuntimeConfigInitializerService } from '@redngapps/feature-flags/data-access';
import { environment } from '@redngapps/feature-flags/env/types';
import { ENVIRONMENT } from '@redngapps/shared/env/types';
import { LoggerService } from '@redngapps/shared/logging/data-access';
import { Logger } from '@redngapps/shared/logging/types';
import {
  matCheckboxDefaultOptions,
  matFormFieldDefaultOptions,
  matRadioDefaultOptions,
  matRippleGlobalOptions,
  matSelectDefaultConfig,
  matTabsDefaultConfig,
  matToggleDefaultConfig,
} from '@redngapps/shared/types';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { isNil } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { appRoutes } from './app.routes';
import { loginFailure, loginSuccess, logoutSuccess } from './core/auth/+state/feature-flags-auth.actions';
import { FeatureFlagsAuthEffects } from './core/auth/+state/feature-flags-auth.effects';
import { FEATURE_FLAGS_AUTH_FEATURE_KEY, featureFlagsAuthReducer } from './core/auth/+state/feature-flags-auth.reducer';
import { AUTH_FORBIDDEN_MESSAGE } from './core/auth/auth-forbidden-message.const';
import { authTokenInterceptor } from './core/auth/interceptor/auth-token.interceptor';
import { isAuthForbidden } from './core/auth/is-auth-forbidden';
import { AuthService } from './core/auth/services/auth.service';

registerLocaleData(localeDe);

export function resetState(reducer: ActionReducer<IFeatureFlagsAppState>): ActionReducer<IFeatureFlagsAppState> {
  return (state, action) => (action.type === logoutSuccess.type ? reducer(undefined, action) : reducer(state, action));
}

/** Keeps the `access_token` out of the Redux DevTools panel (DevTools run on deployed non-prod instances). */
export function redactAccessToken(state: IFeatureFlagsAppState): IFeatureFlagsAppState {
  const authState = state[FEATURE_FLAGS_AUTH_FEATURE_KEY];
  if (isNil(authState) || isNil(authState.accessToken)) {
    return state;
  }

  return { ...state, [FEATURE_FLAGS_AUTH_FEATURE_KEY]: { ...authState, accessToken: '[redacted]' } };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Runtime config must load first (it decides the auth provider), then restore the session.
    provideAppInitializer(() => {
      const runtimeConfigInitializer = inject(RuntimeConfigInitializerService);
      const authService = inject(AuthService);
      const store = inject(Store);
      return runtimeConfigInitializer
        .initialize()
        .then(() => firstValueFrom(authService.restoreSession()))
        .then(session => {
          if (isNil(session)) {
            return undefined;
          }

          // A restored token only authenticates — resolve the user's read/write permissions before
          // logging in, so a reload by an unauthorized user (no `read`) lands on the login page with
          // the reason instead of in the app. Other (e.g. transient) errors leave the user logged out.
          return firstValueFrom(authService.getPermissions(session.accessToken)).then(
            permissions =>
              store.dispatch(
                permissions.read
                  ? loginSuccess({ session, permissions })
                  : loginFailure({ error: AUTH_FORBIDDEN_MESSAGE }),
              ),
            (error: unknown) => {
              if (isAuthForbidden(error)) {
                store.dispatch(loginFailure({ error: AUTH_FORBIDDEN_MESSAGE }));
              }
            },
          );
        });
    }),
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    LoggerService,
    {
      provide: Logger,
      useExisting: LoggerService,
    },
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptors([authTokenInterceptor])),
    provideOAuthClient(),
    provideAnimationsAsync(),
    // Align Material component defaults with the other redngapps apps (client, bdt, VSS):
    // outlined form fields, iconless slide toggles, disabled ripples, etc.
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: matRippleGlobalOptions,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: matFormFieldDefaultOptions,
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: matCheckboxDefaultOptions,
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: matRadioDefaultOptions,
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: matSelectDefaultConfig,
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: matTabsDefaultConfig,
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: matToggleDefaultConfig,
    },
    provideStore({}, { metaReducers: [resetState] }),
    provideState(FEATURE_FLAGS_AUTH_FEATURE_KEY, featureFlagsAuthReducer),
    environment.featureSwitches.devTools === true
      ? provideStoreDevtools({
          maxAge: 25,
          stateSanitizer: redactAccessToken,
        })
      : [],
    provideEffects(FeatureFlagsAuthEffects),
    provideRouter(appRoutes, withHashLocation()),
  ],
};
